import React, { useMemo } from "react";
import DashboardLayout from "../../layouts/DashboardLayout";
import PageHeader from "../../components/PageHeader";
import {
  InboxStackIcon,
  UsersIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { apollo } from "../../utils/apollo";
import { useQuery } from "react-query";
import {
  GET_POLICY_STATUS_COUNT,
  GET_MEMBER_STATUS_COUNT,
  GET_FILE_IMPORT_STATUS_COUNT,
} from "../../utils/graphql";
import { formatNumberInt } from "../../utils/helpers";
import Loading from "../../components/Loading";
import useIntegration from "../../utils/useIntegration";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/style";

function Dashboard() {
  const { t } = useTranslation();
  const { integration, integrationUuid } = useIntegration();
  const isShafafiya = integration?.integrationType === "shafafiya";

  const fetchPolicyStats = useQuery(
    ["policy_status_count", integrationUuid],
    async () => {
      const { data } = await apollo.query({
        query: GET_POLICY_STATUS_COUNT,
        variables: { integrationUuid },
      });
      return data?.getPolicyStatusCount;
    },
    { enabled: !isShafafiya && !!integrationUuid, keepPreviousData: true },
  );

  const fetchMemberStats = useQuery(
    ["member_status_count", integrationUuid],
    async () => {
      const { data } = await apollo.query({
        query: GET_MEMBER_STATUS_COUNT,
        variables: { integrationUuid },
      });
      return data?.getMemberStatusCount;
    },
    { enabled: isShafafiya && !!integrationUuid, keepPreviousData: true },
  );

  const fetchImportStats = useQuery(
    ["file_import_status_count", integrationUuid],
    async () => {
      const { data } = await apollo.query({
        query: GET_FILE_IMPORT_STATUS_COUNT,
        variables: { integrationUuid },
      });
      return data?.getFileImportStatusCount;
    },
    { enabled: !!integrationUuid, keepPreviousData: true },
  );

  const stats = useMemo(() => {
    const entityStats = isShafafiya
      ? fetchMemberStats.data
      : fetchPolicyStats.data;
    const importStats = fetchImportStats.data;

    if (!entityStats) return [];

    const getCount = (list, status) => {
      if (!list) return 0;
      if (Array.isArray(status)) {
        return list
          .filter((item) => status.includes(item.status))
          .reduce((acc, item) => acc + parseInt(item.count), 0);
      }
      return parseInt(list.find((item) => item.status === status)?.count || 0);
    };

    const totalEntities = entityStats.reduce(
      (acc, item) => acc + parseInt(item.count),
      0,
    );
    const completedEntities = getCount(entityStats, [
      "completed",
      "already_exists",
    ]);
    const pendingEntities = getCount(entityStats, [
      "pending",
      "draft",
      "push",
      "check",
      "pushed",
      "linking_to_icp",
    ]);
    const failedEntities = getCount(entityStats, "failed");

    const totalImports =
      importStats?.reduce((acc, item) => acc + parseInt(item.count), 0) || 0;

    return [
      {
        id: 1,
        name: isShafafiya
          ? t("dashboard.total_members", "Total Members")
          : t("dashboard.total_policies", "Total Policies"),
        stat: totalEntities,
        icon: isShafafiya ? UsersIcon : InboxStackIcon,
        variant: "blue",
      },
      {
        id: 2,
        name: t("dashboard.completed", "Completed"),
        stat: completedEntities,
        icon: CheckCircleIcon,
        variant: "emerald",
      },
      {
        id: 3,
        name: t("dashboard.pending", "Pending"),
        stat: pendingEntities,
        icon: ClockIcon,
        variant: "amber",
      },
      {
        id: 4,
        name: t("dashboard.failed", "Failed"),
        stat: failedEntities,
        icon: ExclamationCircleIcon,
        variant: "rose",
      },
      {
        id: 5,
        name: t("dashboard.file_imports", "File Imports"),
        stat: totalImports,
        icon: DocumentDuplicateIcon,
        variant: "purple",
      },
    ];
  }, [
    fetchPolicyStats.data,
    fetchMemberStats.data,
    fetchImportStats.data,
    isShafafiya,
    t,
  ]);

  const isLoading =
    fetchPolicyStats.isLoading ||
    fetchMemberStats.isLoading ||
    fetchImportStats.isLoading;

  return (
    <DashboardLayout>
      <PageHeader
        title={t("dashboard.overview", "Overview")}
        description={t(
          "dashboard.overview_description",
          "A quick summary of your account activity.",
        )}
      />

      <div className="mt-8">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loading />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {stats.map((item) => (
              <StatCard
                key={item.id}
                icon={<item.icon className="w-6 h-6" />}
                title={item.name}
                count={item.stat}
                variant={item.variant}
              />
            ))}
          </div>
        )}

        {!isLoading && stats.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center layer-flat">
            <div className="p-4 rounded-full bg-slate-50 mb-6">
              <ArrowPathIcon className="h-12 w-12 text-slate-300" />
            </div>
            <p className="text-xl font-bold text-slate-900 mb-2">
              {t("dashboard.no_data", "No statistics available yet.")}
            </p>
            <p className="text-sm text-slate-500">
              {t(
                "dashboard.no_data_hint",
                "Please select an integration to view data.",
              )}
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon, title, count, variant }) {
  const variants = {
    rose: {
      iconContainer: "bg-rose-50 text-rose-600 border-rose-100",
      count: "text-rose-600",
      accent: "bg-rose-500",
      shadow: "hover:shadow-rose-500/10",
    },
    blue: {
      iconContainer: "bg-blue-50 text-blue-600 border-blue-100",
      count: "text-blue-600",
      accent: "bg-blue-500",
      shadow: "hover:shadow-blue-500/10",
    },
    amber: {
      iconContainer: "bg-amber-50 text-amber-600 border-amber-100",
      count: "text-amber-600",
      accent: "bg-amber-500",
      shadow: "hover:shadow-amber-500/10",
    },
    emerald: {
      iconContainer: "bg-emerald-50 text-emerald-600 border-emerald-100",
      count: "text-emerald-600",
      accent: "bg-emerald-500",
      shadow: "hover:shadow-emerald-500/10",
    },
    purple: {
      iconContainer: "bg-purple-50 text-purple-600 border-purple-100",
      count: "text-purple-600",
      accent: "bg-purple-500",
      shadow: "hover:shadow-purple-500/10",
    },
  };
  const style = variants[variant] || variants.blue;
  return (
    <div
      className={cn(
        "group relative flex flex-col items-center justify-center bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-slate-200/60 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white active:scale-[0.97]",
        style.shadow,
      )}
    >
      <div
        className={cn(
          "absolute top-0 start-1/2 -translate-x-1/2 h-1 w-0 group-hover:w-16 transition-all duration-300 rounded-b-full",
          style.accent,
        )}
      />
      <div
        className={cn(
          "mb-6 p-4 rounded-2xl border transition-all duration-300 group-hover:scale-110",
          style.iconContainer,
        )}
      >
        {icon}
      </div>
      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 group-hover:text-slate-600 transition-colors text-center">
        {title}
      </div>
      <div
        className={cn(
          "text-4xl font-black tracking-tight transition-all duration-300 group-hover:scale-105",
          style.count,
        )}
      >
        {formatNumberInt(count)}
      </div>
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/40 to-transparent opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity" />
    </div>
  );
}

export default Dashboard;
